/** @jsx jsx */

import {
  Attribute,
  Bucket,
  bucketLabel,
  MapLayerType,
  MarkerStyle,
} from "@cartographerio/atlas-map";
import { Stack } from "@chakra-ui/react";
import { jsx } from "@emotion/react";
import { ReactElement, ReactNode } from "react";
import InspectorItem from "./InspectorItem";

interface SvgProps {
  width: number;
  height: number;
  children: ReactNode;
}

function Svg(props: SvgProps): ReactElement {
  const { width, height, children } = props;
  return (
    <svg
      width={width}
      height={height}
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
    >
      {children}
    </svg>
  );
}

interface MarkerProps {
  layerType: MapLayerType;
  marker: MarkerStyle;
}

function Marker(props: MarkerProps): ReactElement {
  const { layerType, marker } = props;

  const size = 20; // marker.normal.size;
  const strokeWidth = 2;
  const strokeColor = marker.normal.strokeColor;
  const fillColor = marker.normal.fillColor;

  const coord = (m: number) => strokeWidth + m * (size - 2 * strokeWidth);

  switch (layerType) {
    case "LineLayer":
    case "PointLayer":
      return (
        <Svg width={size} height={size}>
          <circle
            cx={coord(0.5)}
            cy={coord(0.5)}
            r={0.5 * (size - 2 * strokeWidth)}
            style={{
              stroke: strokeColor,
              strokeWidth: strokeWidth || 2,
              // strokePosition: "inside", // Can't rely on this being supported everywhere
              fill: fillColor,
            }}
          />
        </Svg>
      );

    case "PolygonLayer":
      return (
        <Svg width={size} height={size}>
          <polygon
            points={`
              ${coord(0.905)},${coord(0.794)}
              ${coord(0.345)},${coord(0.976)}
              ${coord(0.0)},${coord(0.5)}
              ${coord(0.345)},${coord(0.0245)}
              ${coord(0.905)},${coord(0.206)}
            `}
            style={{
              stroke: marker.normal.strokeColor,
              strokeWidth: strokeWidth || 2,
              // strokePosition: "inside", // Can't rely on this being supported everywhere
              fill: marker.normal.fillColor,
            }}
          />
        </Svg>
      );
  }
}

interface LegendEntryProps {
  layerType: MapLayerType;
  marker: MarkerStyle;
  label: string;
}

function LegendEntry(props: LegendEntryProps): ReactElement {
  const { layerType, marker, label } = props;

  return (
    <InspectorItem left={<Marker layerType={layerType} marker={marker} />}>
      {label}
    </InspectorItem>
  );
}

export interface LegendProps {
  layerType: MapLayerType;
  attribute: Attribute;
  buckets: Bucket[];
}

export default function Legend(props: LegendProps): ReactElement {
  const { layerType, attribute, buckets } = props;

  switch (attribute.type) {
    case "SimpleAttribute": {
      return (
        <LegendEntry
          layerType={layerType}
          marker={attribute.marker}
          label="All values"
        />
      );
    }

    case "PropertyAttribute": {
      return (
        <Stack spacing="3">
          {buckets.map((bucket, index) => (
            <LegendEntry
              key={index}
              layerType={layerType}
              marker={bucket.marker}
              label={
                bucketLabel(bucket, attribute.propertyType) ?? "All values"
              }
            />
          ))}
        </Stack>
      );
    }
  }
}
